import React, { useState, useEffect, useContext } from 'react';
import { 
  MessageSquareHeart, 
  Star, 
  X, 
  Send, 
  CheckCircle2, 
  Sparkles, 
  Bug, 
  Lightbulb, 
  Palette, 
  HelpCircle,
  Camera,
  Terminal,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import AuthContext from '../context/AuthContext';
import { getConsoleLogs, getConsoleSummary } from '../utils/consoleRecorder';
import { capturePageSnapshot } from '../utils/pageCapture';

const CATEGORIES = [
  { id: 'opinion', label: 'Lob & Eindruck', icon: Sparkles, color: 'text-amber-500 bg-amber-500/10 border-amber-500/20' },
  { id: 'feature', label: 'Wunsch / Idee', icon: Lightbulb, color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' },
  { id: 'bug', label: 'Fehler melden', icon: Bug, color: 'text-rose-500 bg-rose-500/10 border-rose-500/20' },
  { id: 'design', label: 'Dashboard UX', icon: Palette, color: 'text-sky-500 bg-sky-500/10 border-sky-500/20' },
  { id: 'other', label: 'Sonstiges', icon: HelpCircle, color: 'text-purple-500 bg-purple-500/10 border-purple-500/20' }
];

const RATING_LABELS = {
  1: 'Verbesserungswürdig',
  2: 'Geht so',
  3: 'Ganz gut',
  4: 'Sehr gut',
  5: 'Absolut begeistert!'
};

export default function FeedbackBanner() {
  const { organization, settings } = useSettings();
  const { user } = useContext(AuthContext) || {};

  const [isOpen, setIsOpen] = useState(false);
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [category, setCategory] = useState('opinion');
  const [message, setMessage] = useState('');
  const [authorName, setAuthorName] = useState(user?.name || user?.username || '');
  const [authorEmail, setAuthorEmail] = useState(user?.email || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatusText, setSubmitStatusText] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [showDiagnosticDetails, setShowDiagnosticDetails] = useState(false);
  const [consoleStats, setConsoleStats] = useState({ total: 0, errors: 0, warnings: 0 });

  const stammName = organization?.stammName || settings?.stammName || 'Pfadfinder';
  const isBeta = settings?.isBeta || (typeof window !== 'undefined' && window.location.hostname.includes('beta'));

  // Pre-fill user details if logged in
  useEffect(() => {
    if (user) {
      if (user.name) setAuthorName(user.name);
      if (user.email) setAuthorEmail(user.email);
    }
  }, [user]);

  // Trigger prompt after user explores dashboard for ~20s
  useEffect(() => {
    try {
      const alreadySubmitted = localStorage.getItem('pfadfinder_dash_feedback_submitted');
      if (alreadySubmitted) return;

      const dismissedUntil = localStorage.getItem('pfadfinder_dash_feedback_dismissed_until');
      if (dismissedUntil && Number(dismissedUntil) > Date.now()) return;

      const timer = setTimeout(() => {
        setIsPromptVisible(true);
      }, 20000); // 20 seconds

      return () => clearTimeout(timer);
    } catch (e) {
      console.warn('Dashboard feedback storage access:', e);
    }
  }, []);

  // Update console statistics whenever modal is opened
  useEffect(() => {
    if (isOpen) {
      try {
        setConsoleStats(getConsoleSummary());
      } catch (err) {}
    }
  }, [isOpen]);

  const handleDismissPrompt = (e) => {
    e?.stopPropagation();
    setIsPromptVisible(false);
    try {
      localStorage.setItem('pfadfinder_dash_feedback_dismissed_until', String(Date.now() + 3 * 24 * 60 * 60 * 1000));
    } catch (err) {}
  };

  const handleOpenModal = () => {
    setIsPromptVisible(false);
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() && rating === 0) {
      setErrorMsg('Bitte gib eine kurze Bewertung oder Nachricht ein.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatusText('Erstelle Seiten-Snapshot...');
    setErrorMsg(null);

    // 1. Capture screenshot of the displayed page & rich page/DOM state
    let capturedData = { screenshot: null, pageContext: null };
    try {
      capturedData = await capturePageSnapshot();
    } catch (capErr) {
      console.warn('Page snapshot capture error:', capErr);
    }

    // 2. Collect recorded console messages & runtime exceptions
    setSubmitStatusText('Sende Feedback & Diagnose...');
    let capturedLogs = [];
    try {
      capturedLogs = getConsoleLogs();
    } catch (logErr) {
      console.warn('Console logs collection error:', logErr);
    }

    const payload = {
      rating: rating > 0 ? rating : null,
      category,
      message: message.trim(),
      pageUrl: window.location.href,
      pageTitle: document.title || 'Pfadfinder Dashboard',
      tenantSlug: isBeta ? 'beta' : (window.location.hostname.includes('wutoeschingen') ? 'wutoeschingen' : window.location.hostname.split('.')[0]),
      tenantName: `${stammName} (Dashboard)`,
      authorName: authorName.trim() || (user ? `${user.role || 'User'}` : undefined),
      authorEmail: authorEmail.trim() || undefined,
      screenshot: capturedData.screenshot || undefined,
      consoleLogs: capturedLogs.length > 0 ? capturedLogs : undefined,
      pageContext: capturedData.pageContext || undefined,
      deviceInfo: {
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        userAgent: navigator.userAgent,
        pathname: window.location.pathname,
        userRole: user?.role,
        hasScreenshot: !!capturedData.screenshot,
        consoleLogsCount: capturedLogs.length
      }
    };

    try {
      // 1. Try local worker route /api/feedback
      let res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => null);

      // 2. Direct fallback to Central Manager Hub API
      if (!res || !res.ok) {
        res = await fetch('https://pfadfinder-manager.suppenchris.workers.dev/api/feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (res && res.ok) {
        setIsSuccess(true);
        try {
          localStorage.setItem('pfadfinder_dash_feedback_submitted', 'true');
        } catch (err) {}
        setTimeout(() => {
          setIsOpen(false);
          setIsSuccess(false);
          setMessage('');
          setRating(0);
        }, 3000);
      } else {
        const data = await res?.json().catch(() => ({}));
        throw new Error(data?.error || 'Feedback konnte nicht übermittelt werden');
      }
    } catch (err) {
      console.error('Feedback submit error:', err);
      setErrorMsg('Konnte Feedback nicht senden. Bitte versuche es später noch einmal.');
    } finally {
      setIsSubmitting(false);
      setSubmitStatusText('');
    }
  };

  return (
    <>
      {/* 1. DISCREET FLOATING BUTTON */}
      {!isOpen && (
        <div 
          data-feedback-ignore="true"
          className="fixed bottom-5 left-5 z-40 flex flex-col items-start gap-2 pointer-events-auto"
        >
          {/* Gentle Auto-Prompt Banner / Bubble */}
          {isPromptVisible && (
            <div 
              onClick={handleOpenModal}
              className="animate-bounce-in flex items-center gap-3 p-3.5 pr-4 rounded-2xl bg-surface-containerHighest/95 backdrop-blur-md border border-primary/20 shadow-2xl text-surface-on cursor-pointer hover:border-primary/50 transition-all max-w-sm group"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <MessageSquareHeart className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="text-xs font-bold text-surface-on leading-snug">
                  Feedback zum Dashboard?
                </p>
                <p className="text-[11px] text-surface-onVariant/80 line-clamp-1">
                  Teile deine Ideen oder melde Fehler an die Entwickler.
                </p>
              </div>
              <button
                onClick={handleDismissPrompt}
                title="Später"
                className="p-1 text-surface-onVariant/60 hover:text-surface-on rounded-full hover:bg-surface-on/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Floating Pill Button */}
          <button
            onClick={handleOpenModal}
            className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-surface-containerHighest/90 hover:bg-surface-containerHighest backdrop-blur-md text-surface-on border border-outlineVariant/30 hover:border-primary/40 shadow-lg hover:shadow-primary/10 transition-all hover:scale-105 group font-medium text-xs cursor-pointer"
            title="Feedback zum Dashboard senden"
          >
            <MessageSquareHeart className="w-4 h-4 text-primary group-hover:rotate-12 transition-transform" />
            <span>Feedback</span>
            {isBeta && (
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            )}
          </button>
        </div>
      )}

      {/* 2. FEEDBACK MODAL / FLYOUT CARD */}
      {isOpen && (
        <div 
          data-feedback-modal="true"
          className="feedback-banner-modal fixed inset-0 z-50 flex items-center justify-center p-4 bg-surface-scrim/40 backdrop-blur-sm animate-fade-in"
        >
          <div 
            className="w-full max-w-lg rounded-3xl bg-surface-containerHighest border border-outlineVariant/30 shadow-2xl p-6 sm:p-7 relative overflow-hidden text-surface-on max-h-[92vh] flex flex-col justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Accent Glow */}
            <div className="absolute -top-12 -right-12 w-36 h-36 bg-primary/10 rounded-full blur-2xl pointer-events-none" />

            {/* Header */}
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-outlineVariant/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shadow-inner">
                  <MessageSquareHeart className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-surface-on flex items-center gap-2">
                    Dashboard Feedback
                  </h3>
                  <p className="text-xs text-surface-onVariant">
                    Deine Rückmeldung landet direkt im zentralen Entwicklungs-Hub.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-full text-surface-onVariant hover:text-surface-on hover:bg-surface-on/5 transition-colors"
                title="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {isSuccess ? (
              <div className="py-12 flex flex-col items-center justify-center text-center space-y-3 animate-fade-in">
                <div className="w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-500 flex items-center justify-center animate-bounce">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-surface-on">Vielen Dank!</h4>
                <p className="text-xs text-surface-onVariant max-w-xs">
                  Dein Feedback sowie die Diagnosedaten wurden gespeichert und an die Platform-Administratoren übermittelt.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 pt-4 overflow-y-auto pr-1">
                {/* Rating Section */}
                <div className="space-y-1.5 text-center">
                  <label className="text-xs font-bold text-surface-onVariant uppercase tracking-wider block">
                    Wie zufrieden bist du mit diesem Bereich?
                  </label>
                  <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const active = (hoverRating || rating) >= star;
                      return (
                        <button
                          key={star}
                          type="button"
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => setRating(star)}
                          className="p-2 transition-transform hover:scale-125 focus:outline-none cursor-pointer"
                        >
                          <Star 
                            className={`w-7 h-7 sm:w-8 sm:h-8 transition-colors ${
                              active 
                                ? 'text-amber-400 fill-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]' 
                                : 'text-surface-onVariant/30'
                            }`} 
                          />
                        </button>
                      );
                    })}
                  </div>
                  <div className="h-4 text-[11px] font-semibold text-primary">
                    {RATING_LABELS[hoverRating || rating] || ''}
                  </div>
                </div>

                {/* Category Pills */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-surface-onVariant block">
                    Thema / Kategorie
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      const selected = category === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setCategory(cat.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all border cursor-pointer ${
                            selected
                              ? 'bg-primary text-primary-on border-primary shadow-sm scale-102'
                              : 'bg-surface-containerLow/70 text-surface-onVariant border-outlineVariant/30 hover:border-primary/40'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          <span>{cat.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Message Textarea */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-surface-onVariant block">
                    Deine Nachricht / Vorschlag
                  </label>
                  <textarea
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Beschreibe, was wir verbessern können oder welcher Fehler aufgetreten ist..."
                    className="w-full bg-surface-containerLow text-surface-on px-3.5 py-2.5 rounded-2xl text-xs border border-outlineVariant/30 focus:border-primary/60 focus:outline-none transition-colors resize-none placeholder:text-surface-onVariant/50"
                  />
                </div>

                {/* Optional Contact Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                  <div>
                    <label className="text-[11px] font-medium text-surface-onVariant block mb-1">
                      Dein Name
                    </label>
                    <input
                      type="text"
                      value={authorName}
                      onChange={(e) => setAuthorName(e.target.value)}
                      placeholder="Name (optional)"
                      className="w-full bg-surface-containerLow text-surface-on px-3 py-2 rounded-xl text-xs border border-outlineVariant/30 focus:border-primary/60 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-surface-onVariant block mb-1">
                      E-Mail
                    </label>
                    <input
                      type="email"
                      value={authorEmail}
                      onChange={(e) => setAuthorEmail(e.target.value)}
                      placeholder="E-Mail für Rückfragen"
                      className="w-full bg-surface-containerLow text-surface-on px-3 py-2 rounded-xl text-xs border border-outlineVariant/30 focus:border-primary/60 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Auto Diagnostics Notice Badge */}
                <div className="pt-2">
                  <div className="p-2.5 rounded-2xl bg-surface-containerLow border border-outlineVariant/25 text-[11px] text-surface-onVariant space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-primary font-semibold">
                        <Info className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>Automatische Diagnose enthalten</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowDiagnosticDetails(!showDiagnosticDetails)}
                        className="text-[10px] font-bold text-surface-onVariant hover:text-surface-on flex items-center gap-0.5 cursor-pointer"
                      >
                        <span>{showDiagnosticDetails ? 'Weniger' : 'Details'}</span>
                        {showDiagnosticDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-[10px] text-surface-onVariant/90 pt-0.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-surface-containerHighest border border-outlineVariant/20">
                        <Camera className="w-2.5 h-2.5 text-emerald-500" />
                        <span>Seiten-Snapshot (Screenshot)</span>
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-surface-containerHighest border border-outlineVariant/20">
                        <Terminal className="w-2.5 h-2.5 text-sky-500" />
                        <span>Konsole: {consoleStats.total} Logs {consoleStats.errors > 0 ? `(${consoleStats.errors} Fehler)` : ''}</span>
                      </span>
                    </div>

                    {showDiagnosticDetails && (
                      <div className="pt-1.5 border-t border-outlineVariant/15 text-[10px] space-y-1 font-mono text-surface-onVariant/80 animate-fade-in">
                        <p>• URL: {typeof window !== 'undefined' ? window.location.pathname : '/'}</p>
                        <p>• Auflösung: {typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}px` : ''}</p>
                        <p>• Rolle: {user?.role || 'Admin/User'}</p>
                        <p>• Keine Passwörter oder vertrauliche Zugangsdaten werden übertragen.</p>
                      </div>
                    )}
                  </div>
                </div>

                {errorMsg && (
                  <p className="text-xs text-rose-500 font-semibold text-center bg-rose-500/10 p-2 rounded-xl border border-rose-500/20">
                    {errorMsg}
                  </p>
                )}

                {/* Footer Buttons */}
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-outlineVariant/20">
                  <button
                    type="button"
                    onClick={() => {
                      handleDismissPrompt();
                      setIsOpen(false);
                    }}
                    className="px-4 py-2 text-xs font-semibold text-surface-onVariant hover:text-surface-on rounded-xl hover:bg-surface-on/5 transition-colors cursor-pointer"
                  >
                    Später erinnern
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-5 py-2.5 rounded-2xl bg-primary text-primary-on font-bold text-xs shadow-md hover:shadow-primary/25 hover:bg-primary/90 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-primary-on border-t-transparent animate-spin" />
                        <span>{submitStatusText || 'Wird gesendet...'}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>Feedback absenden</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
